import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  AccordionPanel, Box, Button, Text,
} from 'grommet';
import { FormNext } from 'grommet-icons';
import { Sidebar } from 'react-pro-sidebar';
import { useSpring, animated } from '@react-spring/web';
import {
  FullHeightBox, Container,
} from '@Components/Control';
import { paths } from '@Components/configs';
import { useAppTheme } from '@Components/Context';
import Footer from '../Footer/Footer';
import {
  AuthBanner,
  MenuItemRenderer,
} from './SidenavComponent';
import {
  SIDENAV_WIDTH,
  SIDENAV_COLLAPSED_WIDTH,
  NAVBAR_DEFAULT_HEIGHT,
  SIDENAV_TOP_PAD,
  SIDENAV_PAGE_CONTENTS_TOP_PAD,
} from '../constants';
import { StyledMenu, StyledSidenavAccordion } from './StyledAuthNavWrapper';

// Define navigation sections and items here
const NAV_SECTIONS = [
  {
    label: 'Analytics',
    items: [
      { renderKey: 'cid-analytics', label: 'Creator analytics', path: paths.adminCidAnalytics },
    ],
  },
  {
    label: 'Searches',
    items: [
      { renderKey: 'all-searches', label: 'Manage searches', path: paths.adminSearches },
    ],
  },
  {
    label: 'Results',
    items: [
      { renderKey: 'all-infringements', label: 'Manage results', path: paths.adminResults },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { renderKey: 'compliance', label: 'DMCA notices', path: paths.adminCompliance },
    ],
  },
  {
    label: 'Content ID',
    items: [
      { renderKey: 'manual-claims', label: 'Claims Requests', path: paths.adminManualClaims },
      { renderKey: 'monetization-requests', label: 'Monetization requests', path: paths.adminMonetizationRequests },
      {
        renderKey: 'payout-dashboard',
        label: 'Payout dashboard',
        path: paths.adminPayoutDashboard,
        payoutOnly: true,
      },
      { renderKey: 'cid-payout-setup', label: 'Payout setup status', path: paths.adminCidPayoutStatus },
      { renderKey: 'yt-audits', label: 'YT Audits', path: paths.adminYtAudits },
    ],
  },
  {
    label: 'Demo',
    items: [
      { renderKey: 'demo-links', label: 'Demo Links', path: paths.adminDemoLinks },
    ],
  },
  {
    label: 'Custom Onboarding',
    items: [
      { renderKey: 'custom-onboarding', label: 'Create onboarding link', path: paths.adminCustomOnboarding },
      { renderKey: 'onboarding-tracker', label: 'Onboarding tracker', path: paths.adminOnboardings },
    ],
  },
  {
    label: 'NAGRA Monitoring',
    items: [
      { renderKey: 'nagra-manage-operations', label: 'Manage operations', path: paths.adminNagraManageOperations },
      { renderKey: 'nagra-review-candidates', label: 'Review candidates', path: paths.adminNagraReviewCandidates },
      { renderKey: 'nagra-manual-submission', label: 'Social Media & IPTV', path: paths.adminNagraManualSubmission },
    ],
  },
];

const AdminNavWrapper = ({
  small,
  location,
  bannerProps,
  children,
  smallBanner = true,
  hideBanner = false,
  hideFooter = false,
  hideLeftNav = false,
  payoutDashboardAccess = false,
}) => {
  const [navCollapsed, setNavCollapsed] = React.useState(false);
  const { theme: { navigation, page } } = useAppTheme();

  const locationActiveIndex = NAV_SECTIONS.findIndex((section) => (
    section.items.some((item) => location.pathname.startsWith(item.path))
  ));

  const [activeIndices, setActiveIndices] = React.useState(
    locationActiveIndex !== -1 ? [locationActiveIndex] : [],
  );

  React.useEffect(() => {
    if (locationActiveIndex !== -1) {
      setActiveIndices((prev) => (prev.includes(locationActiveIndex) ? prev : [...prev, locationActiveIndex]));
    }
  }, [locationActiveIndex]);

  const handleAccordionChange = (newIndices) => {
    if (locationActiveIndex !== -1 && !newIndices.includes(locationActiveIndex)) {
      setActiveIndices([...newIndices, locationActiveIndex]);
    } else {
      setActiveIndices(newIndices);
    }
  };

  const { transform } = useSpring({
    transform: `rotate(${navCollapsed ? 180 : 0}deg)`,
    config: { mass: 1.5, tension: 250, friction: 35 },
  });

  const isItemVisible = (item) => !item.payoutOnly || payoutDashboardAccess;

  if (small) {
    return (
      <FullHeightBox background={page.background} pad={{ top: NAVBAR_DEFAULT_HEIGHT }}>
        {!hideBanner && bannerProps && (
          <AuthBanner
            small
            smallBanner={smallBanner}
            title={bannerProps.title}
            subTitle={bannerProps.subTitle}
            bannerChildren={bannerProps.bannerChildren}
            bannerChildrenPosition={bannerProps.bannerChildrenPosition}
            navCollapsed={navCollapsed}
          />
        )}
        <Box flex>
          {children}
        </Box>
        <Footer
          small
          companyMaintenanceSettings={null}
        />
      </FullHeightBox>
    );
  }

  return (
    <FullHeightBox
      background={page.background}
      pad={{ top: smallBanner ? 0 : SIDENAV_PAGE_CONTENTS_TOP_PAD }}
    >
      <Box direction="row">
        {!hideLeftNav && (
          <Sidebar
            collapsed={navCollapsed}
            backgroundColor={navigation.background}
            width={SIDENAV_WIDTH}
            collapsedWidth={SIDENAV_COLLAPSED_WIDTH}
            transitionDuration={300}
            rootStyles={{
              position: 'fixed',
              top: '0',
              height: '100vh',
              borderColor: navigation.border,
              zIndex: 4,
            }}
          >
            <StyledMenu>
              <Box height="100%" direction="column" justify="between" pad={{ top: SIDENAV_TOP_PAD }}>
                <Box pad={{ horizontal: '0.75rem', top: '1.25rem' }}>
                  {navCollapsed ? (
                    <Box direction="column" gap="0.25rem">
                      {NAV_SECTIONS.flatMap((section) => section.items).filter(isItemVisible).map((item) => (
                        <MenuItemRenderer
                          key={item.renderKey}
                          location={location}
                          renderKey={item.renderKey}
                          label={item.label}
                          path={item.path}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                      ))}
                    </Box>
                  ) : (
                    <StyledSidenavAccordion multiple activeIndex={activeIndices} onActive={handleAccordionChange}>
                      {NAV_SECTIONS.map((section) => (
                        <AccordionPanel key={section.label} label={<Text size="0.9rem" weight={600} color={page.primaryText}>{section.label}</Text>}>
                          <Box direction="column" gap="0.25rem" pad={{ bottom: '0.5rem', left: '0.5rem' }}>
                            {section.items.filter(isItemVisible).map((item) => (
                              <MenuItemRenderer
                                key={item.renderKey}
                                location={location}
                                renderKey={item.renderKey}
                                label={item.label}
                                path={item.path}
                                collapsed={false}
                                disabled={false}
                              />
                            ))}
                          </Box>
                        </AccordionPanel>
                      ))}
                    </StyledSidenavAccordion>
                  )}
                </Box>
                <Box justify={navCollapsed ? 'center' : 'end'} align={navCollapsed ? 'center' : 'end'} pad={{ right: navCollapsed ? '0' : '0.5rem' }}>
                  <Button plain onClick={() => setNavCollapsed(!navCollapsed)}>
                    <animated.div style={{ transform, rotate: '180deg' }}>
                      <FormNext size="2.65rem" color={page.highlight} />
                    </animated.div>
                  </Button>
                </Box>
              </Box>
            </StyledMenu>
          </Sidebar>
        )}
        <Container direction="column" height={{ min: '100vh' }}>
          {!hideBanner && bannerProps && (
            <AuthBanner
              small={false}
              smallBanner={smallBanner}
              adjustLeftPad={hideLeftNav}
              title={bannerProps.title}
              subTitle={bannerProps.subTitle}
              bannerChildren={bannerProps.bannerChildren}
              bannerChildrenPosition={bannerProps.bannerChildrenPosition}
              navCollapsed={navCollapsed}
            />
          )}
          <Box flex pad={!hideLeftNav && { left: navCollapsed ? SIDENAV_COLLAPSED_WIDTH : SIDENAV_WIDTH }} style={{ transition: 'all 0.35s ease' }}>
            {children}
          </Box>
          {!hideFooter && (
            <Footer
              small={small}
              companyMaintenanceSettings={null}
            />
          )}
        </Container>
      </Box>
    </FullHeightBox>
  );
};

AdminNavWrapper.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  bannerProps: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    bannerChildren: PropTypes.node,
    bannerChildrenPosition: PropTypes.oneOf(['bottom', 'right']),
  }),
  children: PropTypes.node.isRequired,
  smallBanner: PropTypes.bool,
  hideBanner: PropTypes.bool,
  hideFooter: PropTypes.bool,
  hideLeftNav: PropTypes.bool,
  payoutDashboardAccess: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    payoutDashboardAccess: state.adminAuth.payoutDashboardAccess ?? false,
  };
}

export default connect(mapStateToProps)(AdminNavWrapper);
